import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useModules } from "@/hooks/useModules";
import { analytics } from "@/lib/analytics";
import { sfx } from "@/lib/sfx";
import { MIRROR_DRAWER_ITEMS } from "@/config/navItems";

interface MirrorNavDrawerProps {
  open: boolean;
  onClose: () => void;
  initialModulesOpen?: boolean;
}
const rowClass = "flex min-h-12 w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-foreground/75 hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors motion-reduce:transition-none";

/** One existing hamburger: side-by-side choices on desktop, one pane on touch. */
function NavigationPanel({ onClose, initialModulesOpen = false }: Omit<MirrorNavDrawerProps, "open">) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { visibleModules } = useModules(t);
  const reducedMotion = useReducedMotion();
  const [modulesOpen, setModulesOpen] = useState(initialModulesOpen);
  const moduleTrigger = useRef<HTMLButtonElement>(null);
  const firstModule = useRef<HTMLButtonElement>(null);
  const focusModules = useRef(initialModulesOpen);
  const previousFocus = useRef(document.activeElement as HTMLElement | null);
  const modules = visibleModules.filter(module => module.enabled !== false);

  useEffect(() => {
    if (modulesOpen && focusModules.current) {
      firstModule.current?.focus();
      focusModules.current = false;
    }
  }, [modulesOpen]);
  const showModules = (focus: boolean) => {
    focusModules.current = focus;
    if (modulesOpen && focus) firstModule.current?.focus();
    setModulesOpen(true);
  };
  const backToMenu = () => {
    setModulesOpen(false);
    // The mobile root pane becomes visible in the next render.
    requestAnimationFrame(() => moduleTrigger.current?.focus());
  };
  const goTo = (path: string, moduleId?: string) => {
    if (moduleId) analytics.moduleOpened(moduleId);
    sfx.navigate("module");
    navigate(path, { state: { from: "mirror-drawer" } });
    onClose();
  };

  return <motion.div className="absolute inset-0 z-40"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: reducedMotion ? 0 : 0.15 }}>
    <Dialog.Overlay asChild forceMount>
      <motion.div className="absolute inset-0 bg-black/20" onClick={onClose} />
    </Dialog.Overlay>
    <Dialog.Content asChild forceMount aria-describedby={undefined}
      onOpenAutoFocus={event => {
        if (initialModulesOpen) { event.preventDefault(); firstModule.current?.focus(); }
      }}
      onCloseAutoFocus={event => { event.preventDefault(); if (previousFocus.current?.isConnected) previousFocus.current.focus(); }}
      onEscapeKeyDown={event => { if (modulesOpen) { event.preventDefault(); backToMenu(); } }}>
      <motion.div
        initial={{ y: reducedMotion ? 0 : -12 }} animate={{ y: 0 }} exit={{ y: reducedMotion ? 0 : -12 }}
        transition={{ duration: reducedMotion ? 0 : 0.18 }}
        className={"absolute left-0 right-0 top-0 z-10 max-h-[85dvh] overflow-y-auto overscroll-contain rounded-b-3xl border-b border-foreground/[0.08] shadow-[0_8px_40px_rgba(0,0,0,0.45)] outline-none md:right-auto md:max-w-full md:border-r " + (modulesOpen ? "md:w-[35rem]" : "md:w-[17rem]")}
        style={{ backgroundColor: "hsl(222 38% 7% / 0.97)", paddingTop: "max(16px, env(safe-area-inset-top, 16px))" }}>
        <Dialog.Title className="sr-only">{t("nav.menu")}</Dialog.Title>
        <div className={modulesOpen ? "md:grid md:grid-cols-[15rem_minmax(0,1fr)]" : ""}>
          <nav className={"px-3 pb-4 pt-2 " + (modulesOpen ? "hidden md:block" : "")} aria-label={t("nav.menu")}>
            {MIRROR_DRAWER_ITEMS.map(({ id, path, icon: Icon, labelKey }) => id === "modules" ? (
              <button key={id} ref={moduleTrigger} type="button" aria-expanded={modulesOpen} aria-controls="mirror-modules-menu"
                onClick={() => showModules(true)}
                onPointerEnter={event => { if (event.pointerType === "mouse" && window.matchMedia("(min-width: 768px) and (hover: hover)").matches) showModules(false); }}
                onKeyDown={event => { if (event.key === "ArrowRight") { event.preventDefault(); showModules(true); } }}
                className={rowClass + (modulesOpen ? " bg-foreground/[0.06] text-foreground" : "")}>
                <Icon size={20} strokeWidth={1.6} className="shrink-0 text-primary/80" aria-hidden="true" />
                <span className="text-[15px] font-display font-medium tracking-wide">{t(labelKey)}</span>
                <ChevronRight size={16} className="ml-auto shrink-0" aria-hidden="true" />
              </button>
            ) : (
              <button key={id} type="button" onClick={() => goTo(path)} className={rowClass}>
                <Icon size={20} strokeWidth={1.6} className="shrink-0 text-primary/80" aria-hidden="true" />
                <span className="text-[15px] font-display font-medium tracking-wide">{t(labelKey)}</span>
              </button>
            ))}
          </nav>
          {modulesOpen && <nav id="mirror-modules-menu" aria-label={t("nav.modules")}
            onKeyDown={event => { if (event.key === "ArrowLeft") { event.preventDefault(); backToMenu(); } }}
            className="min-w-0 px-3 pb-4 pt-2 md:border-l md:border-foreground/[0.08]">
            <div className="mb-1 flex min-h-11 items-center gap-2 px-2">
              <button type="button" onClick={backToMenu} aria-label={t("common.back")}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-foreground/80 hover:bg-foreground/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden">
                <ChevronLeft size={22} aria-hidden="true" />
              </button>
              <h2 className="font-display text-sm font-semibold text-foreground">{t("nav.modules")}</h2>
            </div>
            {modules.map(({ id, route, icon: Icon, name }, index) => <button
              key={id} ref={index === 0 ? firstModule : undefined} type="button" onClick={() => goTo(route, id)} className={rowClass}>
              <Icon size={20} strokeWidth={1.6} className="shrink-0 text-primary/80" aria-hidden="true" />
              <span className="text-sm font-medium">{name}</span>
            </button>)}
          </nav>}
        </div>
      </motion.div>
    </Dialog.Content>
  </motion.div>;
}

export default function MirrorNavDrawer({ open, onClose, initialModulesOpen }: MirrorNavDrawerProps) {
  return <Dialog.Root open={open} onOpenChange={next => { if (!next) onClose(); }}>
    <AnimatePresence>{open && <NavigationPanel key="navigation" onClose={onClose} initialModulesOpen={initialModulesOpen} />}</AnimatePresence>
  </Dialog.Root>;
}
